import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { uploadAttachment } from "../../../utils/agentsApi";

/**
 * Shared pending-file upload state for vendor invoice list + guidance editor.
 */
export const useVendorInvoiceUploads = () => {
    const [pendingFiles, setPendingFiles] = useState([]);

    const addFiles = useCallback(async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;

        // Upload sequentially so large invoice batches do not create a burst of
        // presign/S3 PUT requests. The hook owns optimistic progress state only;
        // callers decide when uploaded S3 keys become a submitted invoice job.
        for (const file of files) {
            const id = crypto.randomUUID();
            setPendingFiles((prev) => [
                ...prev,
                { id, name: file.name, state: "uploading", progress: 0 },
            ]);

            try {
                const attachment = await uploadAttachment(file, {
                    onProgress: (pct) => {
                        setPendingFiles((prev) =>
                            prev.map((f) =>
                                f.id === id ? { ...f, progress: Math.round(pct * 100) } : f,
                            ),
                        );
                    },
                });
                setPendingFiles((prev) =>
                    prev.map((f) =>
                        f.id === id
                            ? { ...f, state: "ready", key: attachment.key, progress: 100 }
                            : f,
                    ),
                );
            } catch (err) {
                toast.error(`Upload failed for ${file.name}: ${err.message}`);
                setPendingFiles((prev) => prev.filter((f) => f.id !== id));
            }
        }
    }, []);

    const removeFile = useCallback((id) => {
        setPendingFiles((prev) => prev.filter((f) => f.id !== id));
    }, []);

    const clearFiles = useCallback(() => {
        setPendingFiles([]);
    }, []);

    const readyFiles = pendingFiles.filter((f) => f.state === "ready");
    const uploading = pendingFiles.some((f) => f.state === "uploading");

    return {
        pendingFiles,
        addFiles,
        removeFile,
        clearFiles,
        readyFiles,
        uploading,
    };
};
